import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ROLES } from '../constants/roles';
import { useToast } from '../contexts/ToastContext';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const formatUser = (user) => {
  if (!user) return '';
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return name ? `${name} (${user.email || 'No email'})` : user.email || 'Unknown user';
};

const SpectControl = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [grants, setGrants] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedModuleKey, setSelectedModuleKey] = useState('');
  const [selectedTabKeys, setSelectedTabKeys] = useState([]);
  const [grantAllTabs, setGrantAllTabs] = useState(true);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isSpectator = user?.role === ROLES.SPECTATOR;

  const selectedModule = useMemo(
    () => catalog.find((m) => m.key === selectedModuleKey) || null,
    [catalog, selectedModuleKey]
  );

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [catalogRes, usersRes, grantsRes] = await Promise.all([
        axios.get(`${API_URL}/spect-control/catalog`),
        axios.get(`${API_URL}/spect-control/users`),
        axios.get(`${API_URL}/spect-control/grants`)
      ]);
      setCatalog(catalogRes.data || []);
      setAllUsers(usersRes.data || []);
      setGrants(grantsRes.data || []);
    } catch (error) {
      console.error('Failed to load Spect Control:', error);
      showToast(error.response?.data?.message || 'Failed to load Spect Control', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (!isSpectator) {
      setLoading(false);
      return;
    }
    fetchData();
  }, [isSpectator, fetchData]);

  useEffect(() => {
    setSelectedTabKeys([]);
    setGrantAllTabs(true);
  }, [selectedModuleKey]);

  const handleTabToggle = (tabKey) => {
    setGrantAllTabs(false);
    setSelectedTabKeys((prev) =>
      prev.includes(tabKey) ? prev.filter((k) => k !== tabKey) : [...prev, tabKey]
    );
  };

  const handleGrant = async () => {
    if (!selectedUser) {
      showToast('Please select a user first.', 'warning');
      return;
    }
    if (!selectedModuleKey) {
      showToast('Please select a module.', 'warning');
      return;
    }
    if (!consent) {
      showToast('Please confirm consent before granting access.', 'warning');
      return;
    }
    if (selectedModule?.tabs?.length && !grantAllTabs && selectedTabKeys.length === 0) {
      showToast('Select at least one tab or grant full module access.', 'warning');
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${API_URL}/spect-control/grants`, {
        userId: selectedUser._id,
        moduleKey: selectedModuleKey,
        tabKeys: grantAllTabs || !selectedModule?.tabs?.length ? [] : selectedTabKeys
      });
      showToast('View access granted successfully.', 'success');
      setSelectedUser(null);
      setSelectedModuleKey('');
      setSelectedTabKeys([]);
      setGrantAllTabs(true);
      setConsent(false);
      await fetchData();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to grant view access.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (grantId) => {
    try {
      setSubmitting(true);
      await axios.delete(`${API_URL}/spect-control/grants/${grantId}`);
      showToast('View access revoked.', 'success');
      await fetchData();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to revoke access.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isSpectator) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Only Spectator can access Spect Control.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading Spect Control...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <VisibilityIcon color="primary" />
        <Typography variant="h4" component="h1">
          Spect Control
        </Typography>
      </Stack>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Grant or revoke view-only access to specific modules and tabs for individual users.
        This overrides normal role restrictions — users keep their role but gain extra visibility.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Grant View Access
          </Typography>
          <Stack spacing={2}>
            <Autocomplete
              options={allUsers.filter((u) => u.role !== ROLES.SPECTATOR)}
              value={selectedUser}
              onChange={(_, value) => setSelectedUser(value)}
              getOptionLabel={formatUser}
              isOptionEqualToValue={(option, value) => option._id === value?._id}
              renderInput={(params) => (
                <TextField {...params} label="Select user" placeholder="Search by name or email" />
              )}
            />

            <Autocomplete
              options={catalog}
              value={selectedModule}
              onChange={(_, value) => setSelectedModuleKey(value?.key || '')}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.key === value?.key}
              renderInput={(params) => (
                <TextField {...params} label="Select module" placeholder="Choose a module" />
              )}
            />

            {selectedModule?.tabs?.length > 0 && (
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={grantAllTabs}
                      onChange={(e) => {
                        setGrantAllTabs(e.target.checked);
                        if (e.target.checked) setSelectedTabKeys([]);
                      }}
                    />
                  }
                  label="Grant access to all tabs in this module"
                />
                {!grantAllTabs && (
                  <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                    {selectedModule.tabs.map((tab) => (
                      <Chip
                        key={tab.key}
                        label={tab.label}
                        clickable
                        color={selectedTabKeys.includes(tab.key) ? 'primary' : 'default'}
                        onClick={() => handleTabToggle(tab.key)}
                        variant={selectedTabKeys.includes(tab.key) ? 'filled' : 'outlined'}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            )}

            <FormControlLabel
              control={<Checkbox checked={consent} onChange={(e) => setConsent(e.target.checked)} />}
              label="I confirm granting view-only access to the selected user"
            />

            <Button
              variant="contained"
              onClick={handleGrant}
              disabled={submitting}
              sx={{ alignSelf: 'flex-start' }}
            >
              Grant Access
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Active View Grants ({grants.length})
          </Typography>
          {grants.length === 0 ? (
            <Typography color="text.secondary">No active view grants.</Typography>
          ) : (
            <Stack spacing={2} divider={<Divider flexItem />}>
              {grants.map((grant) => {
                const moduleLabel = catalog.find((m) => m.key === grant.moduleKey)?.label || grant.moduleKey;
                const tabLabels = grant.tabKeys?.length
                  ? grant.tabKeys.map(
                      (key) => catalog.find((m) => m.key === grant.moduleKey)?.tabs?.find((t) => t.key === key)?.label || key
                    )
                  : ['All tabs'];

                return (
                  <Stack
                    key={grant._id}
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Box>
                      <Typography variant="subtitle1">
                        {formatUser(grant.userId)}
                        <Chip size="small" label={grant.userId?.role} sx={{ ml: 1 }} />
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {moduleLabel} — {tabLabels.join(', ')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Granted by {formatUser(grant.grantedBy)} on{' '}
                        {grant.grantedAt ? new Date(grant.grantedAt).toLocaleString() : '—'}
                      </Typography>
                    </Box>
                    <IconButton
                      color="error"
                      aria-label="Revoke access"
                      onClick={() => handleRevoke(grant._id)}
                      disabled={submitting}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Stack>
                );
              })}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SpectControl;
